import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, InputAdornment, MenuItem, Skeleton, Step, StepLabel, Stepper,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { MdArrowBack } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { createBogo, getBogoById, updateBogo } from '../../Webservice/offerAPI';
import OfferImages, { appendImages, savedImages } from './OfferImages';
import { getData } from '../../Webservice/Webservice';
import PRODUCT_ROUTES from '../../API_Route/ProductRoute';
import { FONT, authToken, yellowBtn } from './bogoShared';

const STEPS = ['Basics', 'Buy condition', 'Reward', 'Review'];

const Card = ({ title, hint, accent, children }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.5 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15 }}>{title}</Typography>
    {hint && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 1.5 }}>{hint}</Typography>}
    {children}
  </Box>
);

function CreateBogoOffer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [step, setStep] = useState(0);
  const [loadingOffer, setLoadingOffer] = useState(isEdit);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [filterSub, setFilterSub] = useState('');

  const [wasActive, setWasActive] = useState(true); // editing must not silently switch a paused offer back on
  const [form, setForm] = useState({
    title: '', description: '', startDate: '', endDate: '', visibility: ['b2b', 'b2c'],
    buyType: 'product', items: [], buyQuantity: 1, getQuantity: 1, discountPercentage: 100, images: [],
  });
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };

  // load the offer being edited
  useEffect(() => {
    if (!id) return;
    getBogoById(id)
      .then((res) => {
        const b = res?.data || res?.bogo;
        if (!b) throw new Error('missing');
        const first = b.buyProducts?.[0];
        const buyType = first?.category_id ? 'category' : 'product';
        setWasActive(b.isActive !== false);
        setForm({
          title: b.title || '', description: b.description || '',
          startDate: b.startDate ? String(b.startDate).split('T')[0] : '', endDate: b.endDate ? String(b.endDate).split('T')[0] : '',
          visibility: b.visibility?.length ? b.visibility : ['b2b', 'b2c'],
          buyType,
          items: (b.buyProducts || []).map((p) => p.product_id || p.category_id).filter((x) => x && typeof x === 'object').map((x) => ({ ...x, title: x.title || x.name })),
          buyQuantity: b.buyQuantity || 1, getQuantity: b.getQuantity || 1, discountPercentage: b.discountPercentage ?? 100,
          images: savedImages(b.product_images || []),
        });
      })
      .catch(() => setLoadError('Could not load this offer.'))
      .finally(() => setLoadingOffer(false));
  }, [id]);

  useEffect(() => {
    getData('categories/list?limit=1000', null, authToken(), false)
      .then((res) => setCategories(Array.isArray(res?.categories) ? res.categories : []))
      .catch(() => setCategories([]));
  }, []);
  useEffect(() => {
    if (!filterCat) { setSubcategories([]); return; }
    getData(`subcategories/list?limit=1000&category_id=${filterCat}`, null, authToken(), false)
      .then((res) => setSubcategories(Array.isArray(res?.subcategories) ? res.subcategories : []))
      .catch(() => setSubcategories([]));
  }, [filterCat]);
  useEffect(() => {
    if (form.buyType !== 'product') return;
    setProductsLoading(true);
    const q = new URLSearchParams({ limit: '500' });
    if (filterCat) q.append('categoryId', filterCat);
    if (filterSub) q.append('subcategoryId', filterSub);
    getData(`${PRODUCT_ROUTES.GET_PRODUCT}?${q.toString()}`, null, authToken(), false)
      .then((res) => setProducts(Array.isArray(res?.products) ? res.products : []))
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false));
  }, [filterCat, filterSub, form.buyType]);

  const changeType = (_, v) => {
    if (!v || v === form.buyType) return;
    setForm((f) => ({ ...f, buyType: v, items: [] }));
    setFilterCat('');
    setFilterSub('');
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.title.trim()) e.title = 'Give the offer a name';
      if (form.startDate && form.endDate && form.endDate < form.startDate) e.endDate = 'Must be on or after the start date';
      if (form.visibility.length === 0) e.visibility = 'Choose at least one customer type';
    }
    if (s === 1) {
      if (form.items.length === 0) e.items = `Choose at least one ${form.buyType}`;
      const q = Number(form.buyQuantity);
      if (!Number.isInteger(q) || q < 1) e.buyQuantity = 'Whole number, 1 or more';
    }
    if (s === 2) {
      const q = Number(form.getQuantity);
      if (!Number.isInteger(q) || q < 1) e.getQuantity = 'Whole number, 1 or more';
      const d = Number(form.discountPercentage);
      if (!Number.isFinite(d) || d < 1 || d > 100) e.discountPercentage = 'Between 1 and 100';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const next = () => { if (validateStep(step)) setStep(step + 1); };

  const submit = async () => {
    for (let s = 0; s < 3; s++) if (!validateStep(s)) { setStep(s); return; }
    setSaving(true);
    setSubmitError('');
    try {
      // the reward is the same items as the buy condition
      const targets = form.items.map((x) => (form.buyType === 'product' ? { product_id: x._id } : { category_id: x._id }));
      const payload = new FormData();
      payload.append('title', form.title.trim());
      payload.append('description', form.description.trim());
      payload.append('startDate', form.startDate || '');
      payload.append('endDate', form.endDate || '');
      payload.append('visibility', JSON.stringify(form.visibility));
      payload.append('buyProducts', JSON.stringify(targets));
      payload.append('getProducts', JSON.stringify(targets));
      payload.append('buyQuantity', Number(form.buyQuantity));
      payload.append('getQuantity', Number(form.getQuantity));
      payload.append('discountPercentage', Number(form.discountPercentage));
      if (!isEdit) payload.append('isActive', 'true');
      appendImages(payload, form.images);
      const res = isEdit ? await updateBogo(id, payload) : await createBogo(payload);
      if (!res?.success) throw new Error(res?.message || 'Could not save the offer');
      navigate(isEdit ? `/offers/bogo/view/${id}` : '/offers/bogo');
    } catch (err) {
      setSubmitError(err?.message || 'Could not save the offer.');
    } finally {
      setSaving(false);
    }
  };

  const dealText = useMemo(() => {
    const d = Number(form.discountPercentage);
    return `Buy ${form.buyQuantity || '?'}, get ${form.getQuantity || '?'} ${d >= 100 ? 'free' : `at ${d || '?'}% off`}`;
  }, [form.buyQuantity, form.getQuantity, form.discountPercentage]);

  if (loadingOffer) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={420} sx={{ borderRadius: '14px' }} /></Box>;
  if (loadError) return <Box sx={{ p: 3, maxWidth: 520, mx: 'auto' }}><Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert><Button onClick={() => navigate('/offers/bogo')} sx={yellowBtn}>Back to offers</Button></Box>;

  const options = form.buyType === 'product' ? products : categories;
  const visToggle = (k) => set('visibility', form.visibility.includes(k) ? form.visibility.filter((x) => x !== k) : [...form.visibility, k]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <IconButton onClick={() => navigate(isEdit ? `/offers/bogo/view/${id}` : '/offers/bogo')} sx={{ background: '#f1f5f9' }}><MdArrowBack size={18} /></IconButton>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>{isEdit ? 'Edit BOGO offer' : 'Create BOGO offer'}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>{dealText}</Typography>
        </Box>
      </Box>

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', p: { xs: 2, md: 3 }, mb: 2 }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((l, i) => <Step key={l} completed={i < step}><StepLabel sx={{ cursor: i < step ? 'pointer' : 'default' }} onClick={() => i < step && setStep(i)}>{l}</StepLabel></Step>)}
        </Stepper>
      </Box>

      {step === 0 && (
        <Card title="Offer details" hint="What customers and your team will see" accent="#6366f1">
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField size="small" label="Offer title *" value={form.title} onChange={(e) => set('title', e.target.value)} error={!!errors.title} helperText={errors.title} inputProps={{ maxLength: 120 }} sx={{ gridColumn: { sm: '1 / -1' } }} />
            <TextField size="small" label="Description" multiline rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} sx={{ gridColumn: { sm: '1 / -1' } }} />
            <TextField size="small" type="date" label="Starts" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} InputLabelProps={{ shrink: true }} helperText="Blank means it starts immediately" />
            <TextField size="small" type="date" label="Ends" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} InputLabelProps={{ shrink: true }} error={!!errors.endDate} helperText={errors.endDate || 'Blank means it never ends'} />
          </Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: '#64748b', mt: 2 }}>Who can see it</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControlLabel control={<Checkbox checked={form.visibility.includes('b2c')} onChange={() => visToggle('b2c')} />} label="B2C customers" />
            <FormControlLabel control={<Checkbox checked={form.visibility.includes('b2b')} onChange={() => visToggle('b2b')} />} label="B2B customers" />
          </Box>
          {errors.visibility && <Typography sx={{ color: '#d32f2f', fontSize: 12.5 }}>{errors.visibility}</Typography>}
        </Card>
      )}
      {step === 0 && (
        <Box sx={{ mt: 2 }}>
          <Card title="Offer pictures" hint="Add up to 8. The first is the cover in lists on the website and app." accent="#ec4899">
            <OfferImages items={form.images} onChange={(images) => set('images', images)} error={errors.images} onError={(m) => setErrors((x) => ({ ...x, images: m || undefined }))} />
          </Card>
        </Box>
      )}

      {step === 1 && (
        <Card title="Buy condition" hint="The customer must buy these. The same items are also the reward." accent="#0ea5e9">
          <ToggleButtonGroup exclusive size="small" value={form.buyType} onChange={changeType} sx={{ mb: 2 }}>
            <ToggleButton value="product" sx={{ textTransform: 'none', fontWeight: 700, px: 2.5 }}>Specific products</ToggleButton>
            <ToggleButton value="category" sx={{ textTransform: 'none', fontWeight: 700, px: 2.5 }}>Whole categories</ToggleButton>
          </ToggleButtonGroup>
          {form.buyType === 'product' && (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2 }}>
              <TextField select size="small" label="Narrow by category" value={filterCat} onChange={(e) => { setFilterCat(e.target.value); setFilterSub(''); }}>
                <MenuItem value="">All categories</MenuItem>
                {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.title || c.name}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Narrow by subcategory" value={filterSub} disabled={!filterCat} onChange={(e) => setFilterSub(e.target.value)}>
                <MenuItem value="">All subcategories</MenuItem>
                {subcategories.map((s) => <MenuItem key={s._id} value={s._id}>{s.title || s.name}</MenuItem>)}
              </TextField>
            </Box>
          )}
          <Autocomplete multiple options={options} loading={productsLoading} value={form.items} disableCloseOnSelect filterSelectedOptions
            getOptionLabel={(o) => o.title || o.name || ''} isOptionEqualToValue={(o, v) => o._id === v._id}
            onChange={(_, v) => set('items', v)}
            renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o.title || o.name} {...getTagProps({ index: i })} key={o._id} />)}
            renderInput={(params) => <TextField {...params} size="small" label={form.buyType === 'product' ? 'Products' : 'Categories'} placeholder="Search and select" error={!!errors.items} helperText={errors.items}
              InputProps={{ ...params.InputProps, endAdornment: <>{productsLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />} />
          <TextField size="small" type="number" label="Quantity customer must buy *" value={form.buyQuantity} onChange={(e) => set('buyQuantity', e.target.value)} error={!!errors.buyQuantity} helperText={errors.buyQuantity} inputProps={{ min: 1 }} sx={{ mt: 2, width: { xs: '100%', sm: 280 } }} />
        </Card>
      )}

      {step === 2 && (
        <Card title="Reward" hint="What the customer gets on top of the quantity they bought" accent="#22c55e">
          <Alert severity="info" sx={{ mb: 2 }}>The reward is the same {form.buyType === 'product' ? 'products' : 'categories'} you picked in the buy condition: {form.items.map((i) => i.title || i.name).join(', ') || 'none picked yet'}.</Alert>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField size="small" type="number" label="Quantity given *" value={form.getQuantity} onChange={(e) => set('getQuantity', e.target.value)} error={!!errors.getQuantity} helperText={errors.getQuantity} inputProps={{ min: 1 }} />
            <TextField size="small" type="number" label="Discount on the reward *" value={form.discountPercentage} onChange={(e) => set('discountPercentage', e.target.value)} error={!!errors.discountPercentage} helperText={errors.discountPercentage || '100% means free'}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} inputProps={{ min: 1, max: 100 }} />
          </Box>
        </Card>
      )}

      {step === 3 && (
        <Card title="Review" hint="Check everything, then save" accent="#f59e0b">
          <Box sx={{ p: 2, borderRadius: '12px', background: '#fffbeb', border: '1px solid #fde68a', mb: 2 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: '#92400e' }}>THE DEAL</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, color: '#78350f' }}>{dealText}</Typography>
          </Box>
          {[
            ['Title', form.title], ['Description', form.description],
            ['Runs', `${form.startDate || 'immediately'} → ${form.endDate || 'no end'}`],
            ['Customers', form.visibility.map((v) => v.toUpperCase()).join(' · ')],
            [form.buyType === 'product' ? 'Products' : 'Categories', form.items.map((i) => i.title || i.name).join(', ')],
          ].map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px dashed #e2e8f0' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{k}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{v || '—'}</Typography>
            </Box>
          ))}
          {isEdit && !wasActive && <Alert severity="info" sx={{ mt: 2 }}>This offer is switched off. Saving keeps it off; turn it on from the list when you are ready.</Alert>}
          {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
        </Card>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2.5 }}>
        <Button disabled={step === 0 || saving} onClick={() => setStep(step - 1)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', border: '1px solid #cbd5e1', borderRadius: '10px', px: 3 }}>Back</Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next} variant="contained" sx={yellowBtn}>Next</Button>
        ) : (
          <Button onClick={submit} variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create offer'}</Button>
        )}
      </Box>
    </Box>
  );
}

export default CreateBogoOffer;
